import { inject } from 'inversify';

import { ZONE_TYPE, TRIPS } from '@/constants';
import { service } from '@/decorators/service';
import { FilterManager } from '@/managers/FilterManager';
import { findLeg } from '@/utils/findLeg';
import { IFilter } from '~/IFilter';
import { ITripLeg } from '~/api/ITripLeg';

interface FilterableItem {
  callType?: { id?: string };
  priority?: { id?: string };
  trip?: {
    callType?: { id?: string };
    priority?: { id?: string };
    legs?: ITripLeg[];
  };
  legs?: ITripLeg[];
  pickup?: ITripLeg;
  unit?: { type?: { id?: string | number }; vehicle?: { homePostId?: string } };
  type?: { id?: string | number };
  vehicle?: { type?: { id?: string | number }; homePostId?: string };
}

@service(FilterService)
export class FilterService {
  @inject(FilterManager) private readonly filterManager: FilterManager;

  // Apply active filter to trips (Call Type + Priority + Dispatch Zone)
  applyActiveFilter<T>(data: T[]): T[] {
    const activeFilter = this.filterManager.getActiveFilter;
    if (!activeFilter || !data || data.length === 0) {
      return data;
    }

    return this.processFilters(data, activeFilter);
  }

  // Apply filter by Home Post to resources and scheduler units
  // If filter has homePosts selected, only show units whose homePostId matches
  // If unit has no homePostId (null, undefined, or '0'), always show it (visible to all views)
  applyHomePostFilter<T>(data: T[]): T[] {
    const activeFilter = this.filterManager.getActiveFilter;
    if (!activeFilter || !data || data.length === 0) {
      return data;
    }

    if (!activeFilter.homePosts || activeFilter.homePosts.length === 0) {
      return data;
    }

    return data.filter((item) => {
      const homePostId = this.extractHomePostId(item as FilterableItem);

      if (!homePostId) {
        return true;
      }

      return activeFilter.homePosts?.some((hp) => hp.id?.toString() === homePostId);
    });
  }

  private extractHomePostId(item: FilterableItem): string | null {
    const homePostId = item.unit?.vehicle?.homePostId ?? item.vehicle?.homePostId;

    if (!homePostId || homePostId === '0') {
      return null;
    }

    return homePostId;
  }

  private processFilters<T>(data: T[], filter: IFilter): T[] {
    return data.filter((item) => this.matchesFilter(item as FilterableItem, filter));
  }

  private matchesFilter(item: FilterableItem, filter: IFilter): boolean {
    return (
      this.matchesCallTypeFilter(item, filter) &&
      this.matchesPriorityFilter(item, filter) &&
      this.matchesDispatchZoneFilter(item, filter)
    );
  }

  private matchesCallTypeFilter(item: FilterableItem, filter: IFilter): boolean {
    if (!filter.callTypes || filter.callTypes.length === 0) {
      return true;
    }
    const itemCallType = this.extractCallType(item);
    if (!itemCallType) {
      return true;
    }
    return filter.callTypes.some((ct) => ct.id === itemCallType);
  }

  private matchesPriorityFilter(item: FilterableItem, filter: IFilter): boolean {
    if (!filter.priorities || filter.priorities.length === 0) {
      return true;
    }
    const itemPriority = this.extractPriority(item);
    if (!itemPriority) {
      return true;
    }
    return filter.priorities.some((p) => p.id === itemPriority);
  }

  private matchesDispatchZoneFilter(item: FilterableItem, filter: IFilter): boolean {
    if (!filter.dispatchZones || filter.dispatchZones.length === 0) {
      return true;
    }
    const zoneCode = this.extractDispatchZoneCode(item);

    // Trips with no zone are always visible
    if (!zoneCode) {
      return true;
    }
    return filter.dispatchZones.some((dz) => dz.id === zoneCode);
  }

  private extractDispatchZoneCode(item: FilterableItem): string | null {
    const legs = item.legs ?? item.trip?.legs ?? (item.pickup ? [item.pickup] : null);
    if (!legs) {
      return null;
    }
    const pickupLeg = findLeg(legs, TRIPS.CONTEXT.PICK_UP);
    const zoneCode = pickupLeg?.zones?.find((z) => z.type === 0)?.code;
    return zoneCode ? `${ZONE_TYPE.DISPATCH}_${zoneCode}` : null;
  }

  private extractCallType(item: FilterableItem): string | null {
    return item.callType?.id ?? item.trip?.callType?.id ?? null;
  }

  private extractPriority(item: FilterableItem): string | null {
    return item.priority?.id ?? item.trip?.priority?.id ?? null;
  }
}
